/**
 * -----------------------------------------------------------------------------
 * NCJ COMPONENT: Global Utilities
 * FILE: ncj-utils.js
 * PURPOSE: Shared helper functions and UI components for the No Code Jungle theme.
 *          Includes VIN normalization, field creation, validation, and addon toggle logic.
 * AUTHOR: No Code Jungle (NCJ)
 * LAST MODIFIED: 2026-03-05
 * -----------------------------------------------------------------------------
 */

window.NCJUtils = window.NCJUtils || {};

/**
 * Normalizes a VIN string (uppercase, alphanumeric, I->1, O/Q->0, max 17 chars).
 */
window.NCJUtils.normalizeVin = function (rawValue) {
    if (typeof rawValue !== 'string') return '';
    return rawValue
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .replace(/I/g, "1")
        .replace(/[OQ]/g, "0")
        .slice(0, 17);
};

/**
 * Creates a VIN input field group complete with label, character counter, input, and error placeholder.
 * Wires up the input event listener for normalization.
 * 
 * @param {Object} options 
 * @param {string} options.label - The label text (e.g., "VIN 1" or "New VIN")
 * @param {string} options.id - The unique ID for the input and its associated elements
 * @param {string} options.placeholder - The placeholder text for the input
 * @param {boolean} options.showIcon - Whether to show the tooltip info icon
 * @param {string} options.customClasses - Additional classes for the wrapper
 * @returns {HTMLElement} The constructed wrapper element
 */
window.NCJUtils.createVinFieldGroup = function (options) {
    const { label, id, placeholder, showIcon = false, customClasses = '' } = options;

    const template = document.getElementById('ncj-vin-field-template');
    if (!template) {
        console.error("NCJ Error: VIN field template not found.");
        return document.createElement('div');
    }

    const groupFragment = template.content.cloneNode(true);
    const group = groupFragment.querySelector('.ncj-vin-field-group');

    if (customClasses) {
        customClasses.split(' ').forEach(cls => {
            if (cls) group.classList.add(cls);
        });
    }

    const labelEl = group.querySelector('.ncj-vin-label');
    const inputEl = group.querySelector('.ncj-vin-input');
    const tooltipEl = group.querySelector('.hc-vin-input-tooltip');
    const counterEl = group.querySelector('.ncj-vin-char-counter');
    const errorEl = group.querySelector('.ncj-vin-error');

    if (label && labelEl) {
        labelEl.textContent = label;
        labelEl.style.display = 'block';
        labelEl.setAttribute('for', id);
    }

    inputEl.id = id;
    if (placeholder) {
        inputEl.setAttribute('placeholder', placeholder);
    }

    if (showIcon && tooltipEl) {
        tooltipEl.style.display = 'flex';
    }

    counterEl.id = `${id}-counter`;
    errorEl.id = `${id}-error`;

    const input = inputEl;
    const counter = counterEl;
    const error = errorEl;

    input.addEventListener('input', (e) => {
        let val = window.NCJUtils.normalizeVin(e.target.value);
        e.target.value = val;
        counter.textContent = `${val.length}/17`;
        if (val.length > 0 && val.length < 17) {
            counter.style.color = '#d20000';
        } else {
            counter.style.color = '#6b6b6b';
            error.style.display = 'none';
        }
    });

    return group;
};

/**
 * Validates a container of VIN inputs.
 * Returns an object with { isValid, vins[] }
 */
window.NCJUtils.validateVinContainer = function (container) {
    const inputs = container.querySelectorAll('.ncj-vin-input');
    let hasError = false;
    const vins = [];

    container.querySelectorAll('.ncj-vin-error').forEach(el => el.style.display = 'none');

    inputs.forEach((input) => {
        const val = input.value.trim();
        const errEl = container.querySelector(input.id ? `#${input.id}-error` : '.ncj-vin-error') || input.parentElement.parentElement.querySelector('.ncj-vin-error');

        if (val.length !== 17) {
            hasError = true;
            if (errEl) {
                errEl.textContent = val.length === 0
                    ? (window.vinCheckTranslations?.requireVin || "VIN is required.")
                    : (window.vinCheckTranslations?.min17Char || "VIN must be 17 characters.");
                errEl.style.display = 'block';
            }
        } else {
            vins.push(val);
        }
    });

    return {
        isValid: !hasError,
        vins: vins
    };
};

/* ==========================================================================
   ADDON TOGGLE (UPS Express Upsell) — Shared Logic
   Consumed by: ncj-product-multi-vin.js, ncj-vin-check-product-renderer.js
   ========================================================================== */

(function () {
    var ADDON_HANDLE = "add-on-ups-express-shipping-eu-1-day";
    var ADDON_FALLBACK_ID = "8948325876039";
    var _addonCache = null;
    var _addonFetchPromise = null;
    var _activeAddonInput = null;

    /**
     * Fetches and caches the addon product data (UPS Express).
     * @returns {Promise<{variantId:string, title:string, priceText:string}>}
     */
    window.NCJUtils.loadAddonProduct = function () {
        if (_addonFetchPromise) return _addonFetchPromise;

        _addonFetchPromise = fetch('/products/' + ADDON_HANDLE + '.js')
            .then(function (res) {
                if (!res.ok) throw new Error("Addon product not found");
                return res.json();
            })
            .then(function (product) {
                var variant = product && product.variants && product.variants[0];
                if (!variant || !variant.id) {
                    _addonCache = { variantId: ADDON_FALLBACK_ID, title: "UPS Express + PDF", priceText: "€19,90" };
                    return _addonCache;
                }

                var priceText = "€19,90";
                var locale = window.shopifyLocale || "en-US";
                var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "EUR";
                try {
                    priceText = new Intl.NumberFormat(locale, {
                        style: "currency",
                        currency: currency,
                        minimumFractionDigits: 2
                    }).format(Number(variant.price) / 100);
                } catch (e) {
                    priceText = "€" + (Number(variant.price) / 100).toFixed(2);
                }

                _addonCache = {
                    variantId: variant.id.toString(),
                    title: "UPS Express + PDF",
                    priceText: priceText
                };
                return _addonCache;
            })
            .catch(function () {
                _addonCache = { variantId: ADDON_FALLBACK_ID, title: "UPS Express + PDF", priceText: "€19,90" };
                return _addonCache;
            });

        return _addonFetchPromise;
    };

    /**
     * Creates the addon toggle DOM element.
     * @returns {{row: HTMLElement, input: HTMLInputElement}}
     */
    window.NCJUtils.createAddonToggle = function () {
        var row = document.createElement('div');
        row.className = 'addon-toggle-row ncj-addon-toggle-row';
        row.style.display = 'flex';
        row.style.flexDirection = 'column';

        var addonSubtitleText = (window.vinCheckTranslations && window.vinCheckTranslations.expressShippingApproval) || "Yes, I want it";

        row.innerHTML =
            '<span class="addon-toggle-title" data-addon-toggle-title style="font-size: 13px; color: #555; font-weight: 500; margin-bottom: 4px;">UPS Express + PDF</span>' +
            '<div class="addon-inline" style="display: flex; align-items: center; gap: 8px;">' +
            '<label class="addon-switch">' +
            '<input type="checkbox" class="addon-toggle-input ncj-addon-input">' +
            '<span class="addon-slider"></span>' +
            '</label>' +
            '<span class="addon-toggle-subtitle" data-addon-toggle-subtitle style="font-size: 13px; color: #555; font-weight: 500; cursor: pointer;">' + addonSubtitleText + '</span>' +
            '</div>';

        var input = row.querySelector('.ncj-addon-input');
        var titleEl = row.querySelector('[data-addon-toggle-title]');
        var subtitleEl = row.querySelector('[data-addon-toggle-subtitle]');

        input.disabled = true;

        window.NCJUtils.loadAddonProduct().then(function (result) {
            if (!result || !result.variantId) {
                subtitleEl.textContent = "No disponible";
                return;
            }
            input.disabled = false;
            window.NCJUtils._updateAddonLabel(input, titleEl, result.priceText);
        });

        input.addEventListener('change', function () {
            window.NCJUtils.loadAddonProduct().then(function (result) {
                window.NCJUtils._updateAddonLabel(input, titleEl, result.priceText);
            });
        });

        subtitleEl.addEventListener('click', function () {
            if (input.disabled) return;
            input.checked = !input.checked;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        _activeAddonInput = input;

        return { row: row, input: input };
    };

    /**
     * Registers an externally-created addon input (e.g., one rendered by Liquid SSR).
     * This bridges the gap between server-rendered HTML and the closure's state.
     * @param {HTMLInputElement} input — the checkbox element
     */
    window.NCJUtils.setActiveAddonInput = function (input) {
        _activeAddonInput = input;
    };

    /**
     * Returns the current addon toggle state.
     * @returns {{checked: boolean, variantId: string|null}|null}
     */
    window.NCJUtils.getAddonState = function () {
        var cache = _addonCache;
        var input = _activeAddonInput;
        if (!input || !cache) return null;

        var container = input.closest('[data-ncj-addon-toggle]');
        var vendor = container ? container.getAttribute('data-ncj-vendor') : null;

        return {
            checked: input.checked,
            variantId: cache.variantId,
            vendor: vendor
        };
    };

    /**
     * Updates the addon toggle label text.
     * @param {HTMLInputElement} input
     * @param {HTMLElement} titleEl
     * @param {string} priceText
     */
    window.NCJUtils._updateAddonLabel = function (input, titleEl, priceText) {
        var baseTitle = "UPS Express + PDF";
        if (input.checked) {
            titleEl.textContent = baseTitle + " (+ " + priceText + ")";
        } else {
            titleEl.textContent = baseTitle;
        }
    };

    /**
     * Initializes an addon toggle within a given wrapper (for VIN check results context).
     * This wires up an EXISTING toggle DOM within the wrapper instead of creating a new one.
     * @param {HTMLElement} wrapper - The wrapper containing the pre-rendered toggle
     */
    window.NCJUtils.initExistingAddonToggle = function (wrapper) {
        var row = wrapper.querySelector('.addon-toggle-row');
        var titleEl = wrapper.querySelector('[data-addon-toggle-title]');
        var subtitleEl = wrapper.querySelector('[data-addon-toggle-subtitle]');
        var input = wrapper.querySelector('.addon-toggle-input');

        if (!row || !titleEl || !subtitleEl || !input) return;

        row.style.display = 'flex';
        row.style.flexDirection = 'column';
        input.disabled = true;

        titleEl.style.fontSize = '13px';
        titleEl.style.color = '#555';
        titleEl.style.fontWeight = '500';
        titleEl.style.marginBottom = '4px';
        titleEl.textContent = "UPS Express + PDF";

        var addonSubtitleText = (window.vinCheckTranslations && window.vinCheckTranslations.expressShippingApproval) || "Yes, I want it";
        if (subtitleEl) {
            subtitleEl.textContent = addonSubtitleText;
            subtitleEl.style.fontSize = '13px';
            subtitleEl.style.color = '#555';
            subtitleEl.style.fontWeight = '500';
            subtitleEl.style.cursor = 'pointer';
            subtitleEl.style.marginLeft = '8px';

            subtitleEl.addEventListener('click', function () {
                if (input.disabled) return;
                input.checked = !input.checked;
                input.dispatchEvent(new Event('change', { bubbles: true }));
            });
        }

        window.NCJUtils.loadAddonProduct().then(function (result) {
            if (!result || !result.variantId) {
                if (subtitleEl) subtitleEl.textContent = "No disponible";
                return;
            }
            input.disabled = false;
            window.NCJUtils._updateAddonLabel(input, titleEl, result.priceText);
        });

        input.addEventListener('change', function () {
            window.NCJUtils.loadAddonProduct().then(function (result) {
                window.NCJUtils._updateAddonLabel(input, titleEl, result.priceText);
            });
        });

        _activeAddonInput = input;
    };
})();
